import { forwardRef, useEffect, useImperativeHandle, useRef, type CSSProperties } from 'react';
import SignaturePad, { type PointGroup } from 'signature_pad';

/** The pad methods the signing pages use. */
export interface SignaturePadFieldHandle {
  clear(): void;
  isEmpty(): boolean;
  toDataURL(type?: string): string;
}

interface SignaturePadFieldProps {
  penColor?: string;
  className?: string;
  style?: CSSProperties;
}

interface Size {
  width: number;
  height: number;
}

function scaleStrokes(strokes: PointGroup[], scale: number): PointGroup[] {
  return strokes.map((group) => ({
    ...group,
    points: group.points.map((point) => ({ ...point, x: point.x * scale, y: point.y * scale })),
  }));
}

/**
 * Match the canvas bitmap to its on-screen size without losing the signature.
 *
 * Phones fire `resize` whenever the address bar slides in or out. The pad's
 * own size does not change then, so this returns early. When the pad really
 * changes size (rotation, a narrower window, first layout), the strokes are
 * redrawn, shrunk to fit when the pad got smaller so none of it is clipped.
 */
function fitCanvasToDisplay(
  canvas: HTMLCanvasElement,
  pad: SignaturePad,
  previous: Size | null
): Size {
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  const size = { width: canvas.offsetWidth, height: canvas.offsetHeight };
  const bitmapMatches =
    canvas.width === Math.round(size.width * ratio) &&
    canvas.height === Math.round(size.height * ratio);
  if (
    previous &&
    bitmapMatches &&
    previous.width === size.width &&
    previous.height === size.height
  ) {
    return previous;
  }

  const strokes = pad.toData();
  canvas.width = Math.round(size.width * ratio);
  canvas.height = Math.round(size.height * ratio);
  canvas.getContext('2d')?.scale(ratio, ratio);

  const scale =
    previous && previous.width > 0 && previous.height > 0
      ? Math.min(size.width / previous.width, size.height / previous.height, 1)
      : 1;
  pad.fromData(scale < 1 ? scaleStrokes(strokes, scale) : strokes);
  return size;
}

/**
 * Signature pad for the LOA and completion signing pages, built on signature_pad 5.
 *
 * Replaces react-signature-canvas, whose signature_pad 2.3.2 threw
 * "Cannot read properties of undefined (reading 'push' / 'length')" when a
 * finger moved with no stroke open: after a resize cleared the pad mid-stroke,
 * or when two fingers arrived in one touchstart. Its default clearOnResize also
 * wiped finished signatures when a phone's address bar moved.
 */
export const SignaturePadField = forwardRef<SignaturePadFieldHandle, SignaturePadFieldProps>(
  function SignaturePadField({ penColor = 'black', className, style }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const padRef = useRef<SignaturePad | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Created once per mount; the pen-colour effect below runs straight after.
      const pad = new SignaturePad(canvas);
      padRef.current = pad;

      let size = fitCanvasToDisplay(canvas, pad, null);
      const refit = () => {
        size = fitCanvasToDisplay(canvas, pad, size);
      };
      const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refit);
      if (observer) observer.observe(canvas);
      else window.addEventListener('resize', refit);

      return () => {
        if (observer) observer.disconnect();
        else window.removeEventListener('resize', refit);
        pad.off();
        padRef.current = null;
      };
    }, []);

    useEffect(() => {
      if (padRef.current) padRef.current.penColor = penColor;
    }, [penColor]);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => padRef.current?.clear(),
        isEmpty: () => padRef.current?.isEmpty() ?? true,
        toDataURL: (type = 'image/png') => padRef.current?.toDataURL(type) ?? '',
      }),
      []
    );

    return <canvas ref={canvasRef} className={className} style={style} />;
  }
);
